import { Loader2, Send, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { invokeAi } from "./invokeAi";

interface AnswerRow {
  label: string;
  detail: string;
  link?: string;
}

interface Answer {
  question: string;
  text: string;
  rows: AnswerRow[];
}

const SUGGESTIONS = [
  "Which leads need follow-up today?",
  "Show deals over $10,000 with no recent activity",
  "Who owes us money?",
  "Which leads are hottest?",
  "What appointments do we have this week?",
  "Any unassigned inbox threads?",
  "Which estimates are still pending?",
  "Find duplicate contacts to clean up",
];

/**
 * Gravun Copilot: ask the CRM a question from anywhere.
 *
 * Answers come from a fixed toolbox of vetted reads executed with the
 * CALLER'S JWT (never text-to-SQL), so every row obeys RLS and links go to
 * records the user can actually open. Read-only by design; the one write the
 * AI layer offers (create a task) runs through the approvals capability when
 * the workspace gates it.
 */
export const CopilotDrawer = () => {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const ask = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setQuestion("");
    const { data, error } = await invokeAi<{
      text: string;
      rows: AnswerRow[];
    }>({
      action: "ask",
      question: trimmed,
      workspace_id: getActiveWorkspaceId(),
    });
    setBusy(false);
    const result =
      error || !data ? { text: error ?? "Not reachable", rows: [] } : data;
    setAnswers((previous) => [
      { question: trimmed, text: result.text, rows: result.rows ?? [] },
      ...previous,
    ]);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-8 items-center gap-1 rounded-md px-2 text-[12.5px] text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label="Open Copilot"
        title="Ask Gravun"
      >
        <Sparkles className="size-4" />
        <span className="hidden md:inline">Ask</span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/20">
          <div className="flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="flex items-center gap-1.5 text-[14px] font-semibold">
                <Sparkles className="size-4" />
                Ask Gravun
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => setOpen(false)}
                aria-label="Close Copilot"
              >
                <X className="size-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {answers.length === 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="text-[12.5px] text-muted-foreground">
                    Ask about your workspace. Answers only include what your
                    role can see.
                  </p>
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="rounded-md border border-border px-3 py-2 text-left text-[12.5px] text-muted-foreground hover:bg-accent hover:text-foreground"
                      onClick={() => ask(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {answers.map((answer, index) => (
                    <div key={index} className="flex flex-col gap-1.5">
                      <p className="text-[12.5px] font-medium text-foreground">
                        {answer.question}
                      </p>
                      <p className="text-[12.5px] text-muted-foreground">
                        {answer.text}
                      </p>
                      {answer.rows.length > 0 ? (
                        <ul className="flex flex-col gap-1">
                          {answer.rows.map((row, rowIndex) => (
                            <li
                              key={rowIndex}
                              className="rounded-md border border-border-subtle px-2.5 py-1.5"
                            >
                              {row.link ? (
                                <Link
                                  to={row.link}
                                  onClick={() => setOpen(false)}
                                  className="text-[12.5px] font-medium text-foreground no-underline hover:underline"
                                >
                                  {row.label}
                                </Link>
                              ) : (
                                <span className="text-[12.5px] font-medium">
                                  {row.label}
                                </span>
                              )}
                              <span className="block text-[11.5px] text-muted-foreground">
                                {row.detail}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form
              className="flex items-center gap-2 border-t border-border p-3"
              onSubmit={(event) => {
                event.preventDefault();
                ask(question);
              }}
            >
              <input
                autoFocus
                className="h-9 flex-1 rounded-md border border-input bg-transparent px-3 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Ask about leads, deals, money, campaigns"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
              />
              <Button
                type="submit"
                size="icon"
                className="size-9"
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
              </Button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};
